import React from 'react';
import { Box, Typography, Button, useMediaQuery } from '@mui/material';

import hero from '../assets/image.png';       // Desktop image (invisible, but in DOM)
import front from '../assets/front.png';      // Mobile-only image
import back from '../assets/back.png';        // Mobile-only background
import glasses from '../assets/glasses.png';  // Glasses icon for mobile badge

const Hero = () => {
  const isMobile = useMediaQuery('(max-width:439px)');
  const isDesktop = useMediaQuery('(min-width:440px)');
  const isTablet = useMediaQuery('(min-width:440px) and (max-width:1024px)');

  return (
    <Box
      sx={{
        pt: isMobile ? '0px' : '30px',
        pb: isMobile ? 0 : 8,
        textAlign: 'center',
        mx: 'auto',
        backgroundImage: isMobile
          ? `linear-gradient(
              180deg,
              rgba(0, 0, 0, 0) 50%,
              rgba(0, 0, 0, 0.4) 80%,
              #000000 100%
            ), url(${back})`
          : 'none',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      }}
    >
      {/* Title */}
      <Box>
        {isMobile ? (
          <Typography
            sx={{
              fontFamily: 'Sora, sans-serif',
              fontWeight: 400,
              fontSize: '48px',
              lineHeight: '120%',
              whiteSpace: 'pre-line',
            }}
          >
            The first{'\n'}
            <Box
              component="span"
              sx={{
                fontFamily: 'Unbounded, sans-serif',
                fontWeight: 500,
                fontSize: '48px',
                lineHeight: '120%',
              }}
            >
              Social{'\n'}Persona
            </Box>
            {'\n'}Network.
          </Typography>
        ) : (
          <>
            <Typography
              sx={{
                fontFamily: 'Sora, sans-serif',
                fontWeight: 400,
                fontSize: isTablet ? '60px' : '97.86px',
                lineHeight: '112%',
              }}
            >
              The first{' '}
              <Box
                component="span"
                sx={{
                  fontFamily: 'Unbounded, sans-serif',
                  fontWeight: 500,
                  fontSize: isTablet ? '56px' : '92.74px',
                }}
              >
                Social
              </Box>
            </Typography>

            <Typography
              sx={{
                fontFamily: 'Sora, sans-serif',
                fontWeight: 400,
                fontSize: isTablet ? '60px' : '97.86px',
                lineHeight: '112%',
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: 'Unbounded, sans-serif',
                  fontWeight: 500,
                  fontSize: isTablet ? '56px' : '92.74px',
                }}
              >
                Persona
              </Box>{' '}
              Network.
            </Typography>
          </>
        )}
      </Box>

      {/* Button */}
      {isMobile && (
        <Button
          component="a"
          href="https://apps.apple.com/us/app/auratap/id6754709124"
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          size="large"
          sx={{
            mt: isMobile ? '45px' : '50px',
            height: isMobile ? '53.23px' : '61px',
            fontFamily: 'Sora, sans-serif',
            backgroundColor: '#30D158',
            color: '#FFFFFF',
            borderRadius: isMobile ? '43.44px' : '50px',
            boxShadow: 'none',
            textTransform: 'none',
            fontWeight: isMobile ? 700 : 600,
            fontStyle: 'normal',
            fontSize: isMobile ? '14.77px' : '17px',
            lineHeight: '100%',
            letterSpacing: '0em',
            px: isMobile ? '26.07px' : '30px',
            py: isMobile ? '19.11px' : '22px',
            transition: 'transform 0.3s ease, background-color 0.3s ease',

            '&:hover': {
              backgroundColor: '#30D158',
              boxShadow: 'none',
              transform: 'scale(1.05)',
            },
          }}
        >
          Get App Now
        </Button>
      )}

      {/* Mobile-only image */}
      {isMobile && (
        <Box sx={{ mt: '60px', textAlign: 'center', mx: 'auto', maxWidth: '95%' }}>
          <Box
            component="img"
            src={front}
            alt="Hero Mobile"
            sx={{
              width: '100%',
              height: 'auto',
              borderRadius: '16px',
            }}
          />
        </Box>
      )}

      {/* Invisible image for desktop and tablet */}
      {!isMobile && (
        <Box
          sx={{
            textAlign: 'center',
            mx: 'auto',
            maxWidth: '1200px',
            visibility: 'hidden',
            display: 'block',
          }}
        >
          <Box
            component="img"
            src={hero}
            alt="Hero Desktop"
            sx={{
              width: '100%',
              height: 'auto',
              borderRadius: '32px',
            }}
          />
        </Box>
      )}

      {/* Soon on Apple Vision Pro (mobile only) */}
      {isMobile && (
        <Box
          display="inline-flex"
          alignItems="center"
          sx={{
            border: '1.64px solid #FF9F0A',
            borderRadius: '6.56px',
            color: '#FF9F0A',
            fontFamily: 'Sora, sans-serif',
            fontWeight: 600,
            fontSize: '15.72px',
            px: 2,
            py: 1,
            gap: 1.5,
            mt: 11,
            mb: 1,
            visibility: 'hidden',
          }}
        >
          <img src={glasses} alt="Glasses" width={24} height={14} />
          Soon on Apple Vision Pro
        </Box>
      )}

      {/* Created by (mobile only) */}
      {isMobile && (
        <Typography
          sx={{
            color: '#FFFFFF',
            fontFamily: 'Sora, sans-serif',
            fontWeight: 500,
            fontSize: '16px',
            opacity: 0.6,
            mt: 2,
          }}
        >
          Created by Artur Sychov and Phil Traut
        </Typography>
      )}

      {/* Final paragraph (desktop and tablet only) */}
      {isDesktop && (
        <Box
          sx={{
            textAlign: 'center',
            mx: 'auto',
            maxWidth: isTablet ? '550px' : '900px',
          }}
        >
          <Typography
            sx={{
              color: '#FFFFFF',
              fontFamily: 'Sora, sans-serif',
              fontWeight: 500,
              fontSize: isTablet ? '17.04px' : '28px',
              lineHeight: isTablet ? '30.43px' : '50px',
            }}
          >
            An essential social experience for the Apple Vision Pro
            community to meet, connect, and discover new people
            in the era of Spatial Computing.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Hero;
